// Simplified logger utility for capturing console.log calls and saving them to a local file
class Logger {
  constructor() {
    this.logs = [];
    this.maxLogs = 10000; // Keep last 10,000 logs in memory
    this.isInitialized = false;
    this.originalConsoleLog = console.log;
    this.originalConsoleError = console.error;
    this.originalConsoleWarn = console.warn;
    this.originalConsoleInfo = console.info;

    this.init();
  }

  init() {
    if (this.isInitialized) return;

    // Override console methods to capture logs
    console.log = (...args) => this.captureLog("LOG", ...args);
    console.error = (...args) => this.captureLog("ERROR", ...args);
    console.warn = (...args) => this.captureLog("WARN", ...args);
    console.info = (...args) => this.captureLog("INFO", ...args);

    this.isInitialized = true;

    // Log the logger initialization
    this.originalConsoleLog(
      "🔧 Logger initialized - capturing all console output to memory and local file"
    );
  }

  captureLog(level, ...args) {
    const timestamp = new Date().toISOString();
    const logEntry = {
      timestamp,
      level,
      message: args.map((arg) => this.formatArg(arg)).join(" "),
      rawArgs: args,
      stack: new Error().stack,
    };

    // Add to in-memory logs
    this.logs.push(logEntry);

    // Keep only the last maxLogs entries
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(-this.maxLogs);
    }

    // Call original console method to maintain browser console output
    switch (level) {
      case "LOG":
        this.originalConsoleLog(...args);
        break;
      case "ERROR":
        this.originalConsoleError(...args);
        break;
      case "WARN":
        this.originalConsoleWarn(...args);
        break;
      case "INFO":
        this.originalConsoleInfo(...args);
        break;
      default:
        this.originalConsoleLog(...args);
        break;
    }

    // Save to local file (no downloads)
    this.saveToLocalFile(logEntry);
  }

  formatArg(arg) {
    if (arg === null) return "null";
    if (arg === undefined) return "undefined";

    if (typeof arg === "object") {
      try {
        if (arg instanceof Error) {
          return `Error: ${arg.message}\n${arg.stack}`;
        }
        if (arg instanceof Date) {
          return arg.toISOString();
        }
        if (Array.isArray(arg)) {
          return `[${arg.map((item) => this.formatArg(item)).join(", ")}]`;
        }
        return JSON.stringify(arg, null, 2);
      } catch (e) {
        return `[Object: ${arg.constructor.name}]`;
      }
    }

    return String(arg);
  }

  saveToLocalFile(logEntry) {
    try {
      // Create log entry string
      const logString = `[${logEntry.timestamp}] ${logEntry.level}: ${logEntry.message}\n`;

      // Try to use Node.js fs module if available (for development)
      if (typeof require !== "undefined" && typeof window === "undefined") {
        // Node.js environment - save to file
        const fs = require("fs");
        const path = require("path");
        const logFile = path.join(process.cwd(), "app-logs.txt");

        fs.appendFileSync(logFile, logString);
      } else {
        // Browser environment - just store in memory
        // We can't write to local files from browser for security reasons
        // But we keep everything in memory for debugging
      }
    } catch (error) {
      // If file saving fails, just continue - don't break the app
      // We still have logs in memory
    }
  }

  // Method to get all logs (for debugging purposes)
  getLogs() {
    return [...this.logs];
  }

  // Method to get logs by level
  getLogsByLevel(level) {
    return this.logs.filter((log) => log.level === level);
  }

  // Method to search logs
  searchLogs(query) {
    const searchTerm = query.toLowerCase();
    return this.logs.filter(
      (log) =>
        log.message.toLowerCase().includes(searchTerm) ||
        log.level.toLowerCase().includes(searchTerm)
    );
  }

  // Method to clear logs
  clearLogs() {
    this.logs = [];
    this.originalConsoleLog("🧹 Logs cleared");
  }

  // Method to get log statistics
  getLogStats() {
    const stats = {
      total: this.logs.length,
      byLevel: {},
      byHour: {},
      recentActivity: this.logs.slice(-100), // Last 100 logs
    };

    // Count by level
    this.logs.forEach((log) => {
      stats.byLevel[log.level] = (stats.byLevel[log.level] || 0) + 1;
    });

    // Count by hour
    this.logs.forEach((log) => {
      const hour = new Date(log.timestamp).getHours();
      stats.byHour[hour] = (stats.byHour[hour] || 0) + 1;
    });

    return stats;
  }

  // Method to export logs as text (for debugging in console)
  exportLogsAsText() {
    return this.logs
      .map((log) => `[${log.timestamp}] ${log.level}: ${log.message}`)
      .join("\n");
  }
}

// Create and export the logger instance
const logger = new Logger();

// Export the logger instance and utility functions
export default logger;

// Export utility functions
export const {
  getLogs,
  getLogsByLevel,
  searchLogs,
  clearLogs,
  getLogStats,
  exportLogsAsText,
} = logger;

// Also export the logger class for advanced usage
export { Logger };
